import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, AlertCircle, CheckCircle2, Radio, Wifi, Phone, Network, RefreshCw, MonitorSmartphone, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { useToast } from '@/components/ui/Toast';

type CoaxEpDetail = {
  id: number;
  mac: string;
  voip_mac: string | null;
  wifi_mac: string | null;
  model: string | null;
  note: string | null;
  mode: 'bridge' | 'routed';
  host_limit: number;
  coax_atten_db: number;
  bandwidth_profile_id: number | null;
  bandwidth_profile: { id: number; name: string; up_kbps: number | null; down_kbps: number | null; is_parameterized: boolean } | null;
  shaper_custom_up_kbps: number | null;
  shaper_custom_dn_kbps: number | null;
  wan_mode: 'static' | 'dhcp' | null;
  wan_static_ip: string | null;
  wan_static_mask: string | null;
  wan_static_gw: string | null;
  wan_static_dns1: string | null;
  wan_static_dns2: string | null;
  wan_vlan: number | null;
  lan_nat_enabled: boolean;
  lan_ip: string | null;
  lan_mask: string | null;
  lan_dhcp_start: string | null;
  lan_dhcp_end: string | null;
  wifi_radio_on: boolean;
  wifi_24g_channel: string;
  wifi_24g_mode: string;
  wifi_24g_ssid: string | null;
  wifi_24g_security: string | null;
  voice_enabled: boolean;
  voice_profile_id: number | null;
  voice_profile: { id: number; name: string } | null;
  voip_net_mode: 'static' | 'dhcp' | null;
  voip_static_ip: string | null;
  voip_static_mask: string | null;
  voip_static_gw: string | null;
  voip_static_dns1: string | null;
  voip_static_dns2: string | null;
  sip_user: string | null;
  sip_display_name: string | null;
  last_link_status: 'on' | 'off' | null;
  last_diag_at: string | null;
  last_up_rate_mbps: number | null;
  last_dn_rate_mbps: number | null;
  last_up_atten_db: number | null;
  last_dn_atten_db: number | null;
  provisioning_state: string;
  last_pushed_at: string | null;
  site: { id: number; name: string } | null;
  master: { id: number; name: string | null; management_address: string } | null;
  customer: { id: number; external_id: string | null; name: string | null } | null;
};

export default function CoaxEndpointDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['coax', 'endpoints', id],
    queryFn: () => api.get<{ data: CoaxEpDetail }>(`/coax/endpoints/${id}`).then((r) => r.data.data),
  });

  const [form, setForm] = useState<Partial<CoaxEpDetail> & { sip_password?: string; wifi_24g_psk?: string }>({});
  useEffect(() => {
    if (data) setForm({});
  }, [data]);

  const liveStatus = useMutation({
    mutationFn: () => api.get(`/coax/endpoints/${id}/live-status`).then((r) => r.data as { tables: Array<{ title: string; columns: string[]; rows: string[][] }>; duration_seconds: number; fetched_at: string }),
    onError: (e: any) => toast.push({ kind: 'error', title: 'Refresh failed', detail: e.response?.data?.error ?? e.message }),
  });

  const [range, setRange] = useState<'24h' | '7d' | '30d'>('24h');
  const { data: history } = useQuery({
    queryKey: ['coax', 'endpoints', id, 'diag-history', range],
    queryFn: () => api.get<{ data: Array<{ time: string; up_rate_mbps: number | null; dn_rate_mbps: number | null; up_atten_db: number | null; dn_atten_db: number | null }> }>(`/coax/endpoints/${id}/diag-history?range=${range}`).then((r) => r.data.data),
    refetchInterval: 60_000,
  });

  const probeNow = useMutation({
    mutationFn: () => api.post(`/coax/endpoints/${id}/probe`).then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coax', 'endpoints', id] });
      qc.invalidateQueries({ queryKey: ['coax', 'endpoints', id, 'diag-history'] });
      toast.push({ kind: 'success', title: 'Mesure effectuée', detail: 'Signal capturé via SNMP diag.' });
    },
    onError: (e: any) => toast.push({ kind: 'error', title: 'Probe failed', detail: e.response?.data?.error ?? e.message }),
  });

  const mutation = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch(`/coax/endpoints/${id}`, patch).then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coax', 'endpoints'] });
      setForm({});
      toast.push({ kind: 'success', title: 'Endpoint mis à jour', detail: 'Push au Master en attente (Phase A2).' });
    },
    onError: (e: any) => toast.push({ kind: 'error', title: 'Erreur', detail: e.response?.data?.message ?? e.message }),
  });

  if (isLoading || !data) return <div className="p-8 text-text-faint">Chargement…</div>;

  const view = { ...data, ...form };
  const dirty = Object.keys(form).length > 0;

  const update = <K extends keyof CoaxEpDetail>(k: K, v: any) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title={view.note ? `EP ${view.note}` : `EP ${view.mac}`}
        subtitle={`${view.model ?? 'EoC EP'} · ${view.mac} · ${view.site?.name ?? 'site ?'}`}
        actions={
          <div className="flex gap-2">
            <Link to="/app/coax/endpoints" className="btn btn-ghost btn-sm">
              <ArrowLeft size={12} />Retour
            </Link>
            <button
              className="btn btn-primary btn-sm"
              disabled={!dirty || mutation.isPending}
              onClick={() => mutation.mutate(form)}
            >
              <Save size={12} />Enregistrer
            </button>
          </div>
        }
      />

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Status banner */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <KpiCard label="Link" value={view.last_link_status === 'on' ? 'ON' : 'OFF'} accent={view.last_link_status === 'on' ? 'good' : 'warn'} />
          <KpiCard label="Master" value={view.master?.name ?? view.master?.management_address ?? '—'} />
          <KpiCard label="Client" value={view.customer?.name ?? view.note ?? '—'} sub={view.customer?.external_id ?? undefined} />
          <KpiCard label="Provisioning" value={view.provisioning_state} accent={view.provisioning_state === 'configured' ? 'good' : view.provisioning_state === 'pending' ? 'warn' : 'neutral'} />
        </div>

        {/* Live status (Port Status + Traffic + Host List via HTTP CGI) */}
        <Section title="Live status (Master HTTP CGI)" icon={MonitorSmartphone}>
          <div className="flex items-center gap-2 mb-2">
            <button
              className="btn btn-sm"
              disabled={liveStatus.isPending}
              onClick={() => liveStatus.mutate()}
            >
              <RefreshCw size={11} className={liveStatus.isPending ? 'animate-spin' : ''} />
              {liveStatus.isPending ? 'Polling…' : 'Refresh stats depuis Master'}
            </button>
            {liveStatus.data ? (
              <span className="text-xs text-text-faint">
                Mesuré il y a {Math.round((Date.now() - new Date(liveStatus.data.fetched_at).getTime()) / 1000)}s
                ({liveStatus.data.duration_seconds}s)
              </span>
            ) : null}
          </div>

          {liveStatus.data ? (
            <div className="space-y-3">
              {liveStatus.data.tables.map((t) => (
                <div key={t.title} className="border border-border rounded overflow-hidden">
                  <div className="px-3 py-1 bg-surface-hi text-xs font-medium">{t.title}</div>
                  <table className="w-full text-xs">
                    <thead className="text-text-muted">
                      <tr>{t.columns.map((c, i) => <th key={i} className="px-3 py-1 text-left font-medium">{c || '#'}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {t.rows.length === 0 ? (
                        <tr><td colSpan={t.columns.length} className="px-3 py-2 text-text-faint italic">— vide —</td></tr>
                      ) : t.rows.map((row, i) => (
                        <tr key={i}>{row.map((cell, j) => {
                          // Pretty-print "IP Assigned" column for snooping placeholders
                          const isIpCol = t.columns[j] === 'IP Assigned';
                          let display = cell;
                          let cls = 'px-3 py-1 font-mono';
                          if (isIpCol) {
                            if (cell === 'NA') { display = 'snooping off'; cls += ' text-text-faint italic'; }
                            else if (cell === '0.0.0.0') { display = 'en attente'; cls += ' text-text-faint italic'; }
                          }
                          return <td key={j} className={cls}>{display}</td>;
                        })}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              <div className="text-[10px] text-text-faint">Host List : voip_mac / wifi_mac de l'EP auto-filtrés.</div>
            </div>
          ) : (
            <div className="text-sm text-text-faint italic">Click "Refresh" pour récupérer port status + traffic + hosts downstream du Master.</div>
          )}
        </Section>

        {/* Signal diag with graph */}
        <Section title="Signal coax (mesure SNMP)" icon={Activity}>
          <div className="flex items-center gap-2 mb-3">
            <button className="btn btn-sm" disabled={probeNow.isPending} onClick={() => probeNow.mutate()}>
              <Radio size={11} className={probeNow.isPending ? 'animate-pulse' : ''} />
              {probeNow.isPending ? 'Mesure…' : 'Mesurer maintenant'}
            </button>
            <div className="flex gap-1 ml-3">
              {(['24h', '7d', '30d'] as const).map((r) => (
                <button
                  key={r}
                  className={`px-2 py-0.5 text-xs rounded ${range === r ? 'bg-surface-hi text-text' : 'text-text-muted hover:bg-surface-hi/50'}`}
                  onClick={() => setRange(r)}
                >{r}</button>
              ))}
            </div>
            {view.last_diag_at ? (
              <span className="text-xs text-text-faint ml-auto">Dernière mesure : {new Date(view.last_diag_at).toLocaleString('fr-CA')}</span>
            ) : null}
          </div>

          {view.last_diag_at ? (
            <div className="grid grid-cols-4 gap-3 text-sm mb-3">
              <Mini label="UpLink rate" value={`${view.last_up_rate_mbps} Mbps`} />
              <Mini label="DnLink rate" value={`${view.last_dn_rate_mbps} Mbps`} />
              <Mini label="UpLink atten" value={`${view.last_up_atten_db} dB`} />
              <Mini label="DnLink atten" value={`${view.last_dn_atten_db} dB`} />
            </div>
          ) : (
            <div className="text-text-faint text-sm italic mb-3">Pas encore mesuré — click "Mesurer maintenant".</div>
          )}

          {history && history.length > 1 ? (
            <>
              <div className="text-xs text-text-muted mb-1">Rates (Mbps)</div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={history.map((p) => ({ t: new Date(p.time).getTime(), up: p.up_rate_mbps, dn: p.dn_rate_mbps }))}>
                  <CartesianGrid stroke="#2a2d33" strokeDasharray="3 3" />
                  <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} scale="time"
                    tickFormatter={(t) => new Date(t).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                    stroke="#6c7079" fontSize={10} />
                  <YAxis stroke="#6c7079" fontSize={10} unit=" Mbps" />
                  <Tooltip labelFormatter={(t) => new Date(Number(t)).toLocaleString('fr-CA')}
                    contentStyle={{ background: '#1a1d22', border: '1px solid #2a2d33', fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="up" name="Up" stroke="#34d399" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="dn" name="Dn" stroke="#60a5fa" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>

              <div className="text-xs text-text-muted mb-1 mt-3">Attenuation (dB)</div>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={history.map((p) => ({ t: new Date(p.time).getTime(), upA: p.up_atten_db, dnA: p.dn_atten_db }))}>
                  <CartesianGrid stroke="#2a2d33" strokeDasharray="3 3" />
                  <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} scale="time"
                    tickFormatter={(t) => new Date(t).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                    stroke="#6c7079" fontSize={10} />
                  <YAxis stroke="#6c7079" fontSize={10} unit=" dB" />
                  <Tooltip labelFormatter={(t) => new Date(Number(t)).toLocaleString('fr-CA')}
                    contentStyle={{ background: '#1a1d22', border: '1px solid #2a2d33', fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="upA" name="Up atten" stroke="#fbbf24" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="dnA" name="Dn atten" stroke="#f87171" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="text-text-faint text-xs italic">Pas assez d'historique pour le graph (besoin de ≥2 points). Cron hourly accumule.</div>
          )}
        </Section>

        {/* Identité */}
        <Section title="Identité" icon={Network}>
          <Field label="Note (= customer external_id)" value={view.note ?? ''} onChange={(v) => update('note', v || null)} />
          <Field label="MAC EP" value={view.mac} readOnly />
          <Field label="VoIP MAC" value={view.voip_mac ?? ''} readOnly />
          <Field label="WiFi MAC" value={view.wifi_mac ?? ''} readOnly />
        </Section>

        {/* Mode + coax */}
        <Section title="Mode coax">
          <Select label="Mode" value={view.mode} onChange={(v) => update('mode', v)}
            options={[{value:'bridge',label:'Bridge (transparent)'},{value:'routed',label:'Routed (router NAT + WiFi)'}]} />
          <Select label="HostLimit" value={String(view.host_limit)} onChange={(v) => update('host_limit', Number(v))}
            options={[
              {value:'0',label:'0 — Block'},{value:'1',label:'1'},{value:'2',label:'2'},{value:'3',label:'3'},
              {value:'4',label:'4'},{value:'5',label:'5'},{value:'6',label:'6'},{value:'7',label:'7 — Unlimit'},
            ]} />
          <Select label="Atténuation EP" value={String(view.coax_atten_db)} onChange={(v) => update('coax_atten_db', Number(v))}
            options={[{value:'0',label:'-0 dB'},{value:'3',label:'-3 dB'},{value:'6',label:'-6 dB'},{value:'9',label:'-9 dB'}]} />
        </Section>

        {/* Bandwidth */}
        <Section title="Forfait bandwidth">
          <div className="text-sm">
            Profile actuel : <span className="font-medium">{view.bandwidth_profile?.name ?? <span className="text-text-faint italic">aucun</span>}</span>
            {view.bandwidth_profile?.up_kbps != null ? (
              <span className="text-text-muted text-xs ml-2">({Math.round(view.bandwidth_profile.up_kbps / 1000)} / {Math.round((view.bandwidth_profile.down_kbps ?? 0) / 1000)} Mbps)</span>
            ) : null}
          </div>
          <div className="text-xs text-text-faint mt-2">Édition du forfait : sélecteur dans Phase A5.2 (besoin de la liste catalogue + custom override pour parameterized).</div>
        </Section>

        {/* WAN (routed only) */}
        {view.mode === 'routed' && (
          <Section title="WAN (mode routé)">
            <Select label="WAN mode" value={view.wan_mode ?? 'dhcp'} onChange={(v) => update('wan_mode', v)}
              options={[{value:'dhcp',label:'DHCP'},{value:'static',label:'Static'}]} />
            {view.wan_mode === 'static' && (
              <>
                <Field label="WAN IP" value={view.wan_static_ip ?? ''} onChange={(v) => update('wan_static_ip', v || null)} />
                <Field label="WAN Mask" value={view.wan_static_mask ?? ''} onChange={(v) => update('wan_static_mask', v || null)} />
                <Field label="WAN Gateway" value={view.wan_static_gw ?? ''} onChange={(v) => update('wan_static_gw', v || null)} />
                <Field label="DNS 1" value={view.wan_static_dns1 ?? ''} onChange={(v) => update('wan_static_dns1', v || null)} />
                <Field label="DNS 2" value={view.wan_static_dns2 ?? ''} onChange={(v) => update('wan_static_dns2', v || null)} />
              </>
            )}
            <Field label="VLAN (1-4094)" type="number" value={view.wan_vlan ?? ''} onChange={(v) => update('wan_vlan', v ? Number(v) : null)} />
          </Section>
        )}

        {/* LAN + NAT (routed only) */}
        {view.mode === 'routed' && (
          <Section title="LAN + NAT (mode routé)">
            <Toggle label="NAT enabled" checked={view.lan_nat_enabled} onChange={(v) => update('lan_nat_enabled', v)} />
            {view.lan_nat_enabled && (
              <>
                <Field label="LAN IP (gateway interne)" value={view.lan_ip ?? ''} onChange={(v) => update('lan_ip', v || null)} />
                <Field label="LAN Mask" value={view.lan_mask ?? ''} onChange={(v) => update('lan_mask', v || null)} />
                <Field label="DHCP range start" value={view.lan_dhcp_start ?? ''} onChange={(v) => update('lan_dhcp_start', v || null)} />
                <Field label="DHCP range end" value={view.lan_dhcp_end ?? ''} onChange={(v) => update('lan_dhcp_end', v || null)} />
              </>
            )}
          </Section>
        )}

        {/* WiFi 2.4G */}
        {view.mode === 'routed' && (
          <Section title="WiFi 2.4 GHz" icon={Wifi}>
            <Toggle label="Radio ON" checked={view.wifi_radio_on} onChange={(v) => update('wifi_radio_on', v)} />
            {view.wifi_radio_on && (
              <>
                <Field label="SSID" value={view.wifi_24g_ssid ?? ''} onChange={(v) => update('wifi_24g_ssid', v || null)} />
                <Select label="Security" value={view.wifi_24g_security ?? 'wpa2'} onChange={(v) => update('wifi_24g_security', v)}
                  options={[{value:'none',label:'Open'},{value:'wpa',label:'WPA'},{value:'wpa2',label:'WPA2'},{value:'wpa3',label:'WPA3'}]} />
                <Field label="PSK (laisser vide pour ne pas changer)" type="password" value={form.wifi_24g_psk ?? ''} onChange={(v) => setForm((f) => ({ ...f, wifi_24g_psk: v }))} />
                <Field label="Channel" value={view.wifi_24g_channel} onChange={(v) => update('wifi_24g_channel', v)} />
              </>
            )}
          </Section>
        )}

        {/* Voice */}
        <Section title="Voice (SIP)" icon={Phone}>
          <Toggle label="Voice enabled" checked={view.voice_enabled} onChange={(v) => update('voice_enabled', v)} />
          {view.voice_enabled && (
            <>
              <div className="text-sm">Profil voice : <span className="font-medium">{view.voice_profile?.name ?? '—'}</span></div>
              <Select label="VoIP net mode" value={view.voip_net_mode ?? 'dhcp'} onChange={(v) => update('voip_net_mode', v)}
                options={[{value:'dhcp',label:'DHCP'},{value:'static',label:'Static'}]} />
              {view.voip_net_mode === 'static' && (
                <>
                  <Field label="VoIP IP" value={view.voip_static_ip ?? ''} onChange={(v) => update('voip_static_ip', v || null)} />
                  <Field label="VoIP Mask" value={view.voip_static_mask ?? ''} onChange={(v) => update('voip_static_mask', v || null)} />
                  <Field label="VoIP Gateway" value={view.voip_static_gw ?? ''} onChange={(v) => update('voip_static_gw', v || null)} />
                </>
              )}
              <Field label="SIP User (= phone number)" value={view.sip_user ?? ''} onChange={(v) => update('sip_user', v || null)} />
              <Field label="SIP Display Name" value={view.sip_display_name ?? ''} onChange={(v) => update('sip_display_name', v || null)} />
              <Field label="SIP Password (laisser vide pour ne pas changer)" type="password" value={form.sip_password ?? ''} onChange={(v) => setForm((f) => ({ ...f, sip_password: v }))} />
            </>
          )}
        </Section>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: 'good' | 'warn' | 'neutral' }) {
  const color = accent === 'good' ? 'text-emerald-400' : accent === 'warn' ? 'text-amber-400' : 'text-text';
  return (
    <div className="card card-pad">
      <div className="text-xs text-text-muted">{label}</div>
      <div className={`text-sm font-medium ${color}`}>{value}</div>
      {sub ? <div className="text-xs text-text-faint">{sub}</div> : null}
    </div>
  );
}

function Section({ title, icon: Icon, children }: { title: string; icon?: any; children: React.ReactNode }) {
  return (
    <div className="card card-pad space-y-2.5">
      <div className="flex items-center gap-2 text-sm font-semibold border-b border-border pb-1.5 mb-2">
        {Icon ? <Icon size={14} className="text-text-muted" /> : null}
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, readOnly, type = 'text' }: {
  label: string; value: string | number; onChange?: (v: string) => void; readOnly?: boolean; type?: string;
}) {
  return (
    <div className="grid grid-cols-3 gap-3 items-center">
      <label className="text-xs text-text-muted col-span-1">{label}</label>
      <input
        type={type}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
        className={`col-span-2 input ${readOnly ? 'opacity-60' : ''}`}
      />
    </div>
  );
}

function Select({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[];
}) {
  return (
    <div className="grid grid-cols-3 gap-3 items-center">
      <label className="text-xs text-text-muted col-span-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="col-span-2 input">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-text-faint text-xs">{label}</div>
      <div className="font-mono">{value}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-center">
      <label className="text-xs text-text-muted col-span-1">{label}</label>
      <div className="col-span-2">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mr-2" />
        <span className="text-sm">{checked ? 'Activé' : 'Désactivé'}</span>
      </div>
    </div>
  );
}
